import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  beautyDeleteServiceApi,
  beautyListProfessionalsApi,
  beautyListServicesApi,
  beautyReorderServicesApi,
  beautyUpdateServiceApi,
} from "../../api/organizerBeautyApi";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import useCobrosHabilitados from "../hooks/useCobrosHabilitados";
import ServicioForm from "./ServicioForm";
import { MSG_ERROR_MODO, sincronizarModoReserva } from "./salonModo";
import { ConfirmModal, OrgToast } from "./TurnosUi";
import { idDe } from "./profesionalPayload";
import { resumenDeServicio } from "./opcionesDeServicio";
import { precioDesde, rangoDeDuracion, textoAdicionales, textoVariantes } from "../../helpers/beautyLineas";
import "./turnos-beauty.css";
import "../reservas/reservas-organizer.css";

/* ─────────────────────────────────────────
   ServiciosTab — catálogo del salón (GET /services), agrupado por categoría.
   Diseño inspirado en el prototipo Zoco Beauty (Artboard 5).
───────────────────────────────────────── */

const SIN_CATEGORIA = "Sin categoría";
const plata = (n) => `$ ${Number(n || 0).toLocaleString("es-AR")}`;

const AVATAR_COLORS = ["zr-avatar--c1", "zr-avatar--c2", "zr-avatar--c3", "zr-avatar--c4", "zr-avatar--c5"];

const agrupar = (items) => {
  const grupos = new Map();
  items.forEach((s) => {
    const clave = (s.category || "").trim() || SIN_CATEGORIA;
    if (!grupos.has(clave)) grupos.set(clave, []);
    grupos.get(clave).push(s);
  });
  return Array.from(grupos, ([categoria, servicios]) => ({ categoria, servicios }));
};

/** `onSalonChange(patch)` lets the module keep the salon fresh when the deposit mode is re-derived. */
const ServiciosTab = ({ salon, onSalonChange }) => {
  const [items, setItems] = useState([]);
  const [profesionales, setProfesionales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [editor, setEditor] = useState(null); // { servicio: obj | null }
  const [aBorrar, setABorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);
  const [toast, setToast] = useState("");
  const { cargando, puedeCobrar } = useCobrosHabilitados();
  const senasBloqueadas = cargando || !puedeCobrar;

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [resSrv, resPro] = await Promise.all([
        beautyListServicesApi(),
        beautyListProfessionalsApi().catch(() => ({ data: { info: [] } })),
      ]);
      const listaSrv = Array.isArray(resSrv?.data?.info) ? resSrv.data.info : [];
      const listaPro = Array.isArray(resPro?.data?.info) ? resPro.data.info : [];
      setItems(listaSrv);
      setProfesionales(listaPro);
      return listaSrv;
    } catch (e) {
      setError(e?.response?.data?.message || "No pudimos cargar tus servicios. Probá de nuevo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cerrarToast = useCallback(() => setToast(""), []);

  // Filter items by search query
  const itemsFiltrados = useMemo(() => {
    if (!busqueda.trim()) return items;
    const q = busqueda.toLowerCase().trim();
    return items.filter(
      (s) =>
        (s.name || "").toLowerCase().includes(q) ||
        (s.category || "").toLowerCase().includes(q) ||
        (s.description || "").toLowerCase().includes(q)
    );
  }, [items, busqueda]);

  const grupos = useMemo(() => agrupar(itemsFiltrados), [itemsFiltrados]);
  const categorias = useMemo(
    () => Array.from(new Set(items.map((s) => (s.category || "").trim()).filter(Boolean))),
    [items]
  );

  // The deposit mode follows the deposits of the catalog: after any change, re-derive it (never blocks the save).
  const sincronizarModo = async (servicios) => {
    if (!servicios) return;
    const r = await sincronizarModoReserva(salon, { puedeCobrar, cargando, servicios });
    if (r.patch && onSalonChange) onSalonChange(r.patch);
    if (r.error) setToast(MSG_ERROR_MODO);
  };

  const guardado = async (mensaje) => {
    setEditor(null);
    setToast(mensaje);
    await sincronizarModo(await cargar());
  };

  const alternarActivo = async (s) => {
    const nuevoActivo = s.active === false ? true : false;
    try {
      await beautyUpdateServiceApi({ id: s._id, active: nuevoActivo });
      setItems((prev) => prev.map((item) => (item._id === s._id ? { ...item, active: nuevoActivo } : item)));
    } catch (e) {
      setToast(e?.response?.data?.message || "No pudimos cambiar el estado del servicio.");
    }
  };

  const confirmarBorrado = async () => {
    setBorrando(true);
    try {
      await beautyDeleteServiceApi(aBorrar._id);
      setABorrar(null);
      setEditor((e) => (e?.servicio?._id === aBorrar._id ? null : e));
      setToast("Servicio eliminado");
      await sincronizarModo(await cargar());
    } catch (e) {
      setABorrar(null);
      setToast(e?.response?.data?.message || "No pudimos eliminar el servicio. Probá de nuevo.");
    } finally {
      setBorrando(false);
    }
  };

  // Moves a service one place within its category; the order is global, so it swaps with its neighbour.
  const mover = async (servicio, delta) => {
    const grupo = grupos.find((g) => g.servicios.includes(servicio));
    if (!grupo) return;
    const vecino = grupo.servicios[grupo.servicios.indexOf(servicio) + delta];
    if (!vecino) return;
    const previo = items;
    const nuevo = items.slice();
    const i = nuevo.indexOf(servicio);
    const j = nuevo.indexOf(vecino);
    [nuevo[i], nuevo[j]] = [nuevo[j], nuevo[i]];
    setItems(nuevo);
    try {
      await beautyReorderServicesApi(nuevo.map((s) => s._id));
    } catch (e) {
      setItems(previo);
      setToast(e?.response?.data?.message || "No pudimos cambiar el orden. Probá de nuevo.");
    }
  };

  // Helper to get assigned professionals for a service
  const obtenerProsDelServicio = (sId) => {
    const sid = String(sId);
    return profesionales.filter((p) => {
      const ids = (p.serviceIds || p.services || []).map(idDe);
      return ids.includes(sid);
    });
  };

  // Helper to check if any professional has custom override for this service
  const contarOverrides = (sId) => {
    const sid = String(sId);
    return profesionales.filter((p) => {
      return (p.serviceOverrides || []).some(
        (o) => idDe(o.serviceId) === sid && (o.price != null || o.durationMinutes != null)
      );
    }).length;
  };

  if (loading) return <SpinLoading />;

  if (error) {
    return (
      <div className="org-page-error">
        <p>{error}</p>
        <button type="button" onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const editando = editor?.servicio ? items.find((x) => x._id === editor.servicio._id) || editor.servicio : null;

  // Secondary actions of the open service: reorder within its category and delete (with confirmation).
  const accionesDe = (s) => {
    const grupo = grupos.find((g) => g.servicios.includes(s));
    const idx = grupo ? grupo.servicios.indexOf(s) : -1;
    return (
      <>
        <button type="button" className="zr-btn zr-btn--ghost" aria-label={`Subir ${s.name}`} disabled={idx <= 0} onClick={() => mover(s, -1)}>
          ↑ Subir
        </button>
        <button
          type="button"
          className="zr-btn zr-btn--ghost"
          aria-label={`Bajar ${s.name}`}
          disabled={!grupo || idx === grupo.servicios.length - 1}
          onClick={() => mover(s, 1)}
        >
          ↓ Bajar
        </button>
        <button type="button" className="zr-btn zr-btn--ghost zr-side-actions__danger" aria-label={`Eliminar ${s.name}`} onClick={() => setABorrar(s)}>
          Eliminar
        </button>
      </>
    );
  };

  return (
    <div>
      {items.length === 0 ? (
        <div className="zr-panel" style={{ padding: 0 }}>
          <div className="zr-empty" style={{ flexDirection: "column", gap: 12, textAlign: "center", padding: 24 }}>
            <p style={{ margin: 0 }}>Todavía no cargaste servicios.</p>
            <p style={{ margin: 0 }}>Cada servicio lleva su duración y su precio; son los que tus clientes eligen al reservar.</p>
            <button type="button" className="zr-btn zr-btn--dark" onClick={() => setEditor({ servicio: null })}>
              Agregá tu primer servicio
            </button>
          </div>
        </div>
      ) : (
        <div className="zr-panel">
          <div className="zr-section-head bt-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div>
              <h2 className="zr-section-head__title">Servicios</h2>
              <p className="zr-section-head__hint">| El precio y la duración propios de cada profesional se editan en Profesionales.</p>
            </div>
            <button type="button" className="zr-btn zr-btn--dark" onClick={() => setEditor({ servicio: null })}>
              Agregar servicio
            </button>
          </div>

          <div className="zr-table-toolbar">
            <div className="zr-search-wrap">
              <svg className="zr-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
              <input
                type="search"
                className="ef-input zr-search-input"
                aria-label="Buscar servicio"
                placeholder="Buscar servicio..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <p className="ef-hint" style={{ margin: 0 }}>
              La seña de cada servicio se cobra al reservar cuando tus cobros están validados.
            </p>
          </div>

          <div className="zr-table-wrap">
            <table className="zr-table zr-table--servicios">
              <thead>
                <tr>
                  <th>Servicio</th>
                  <th>Duración</th>
                  <th>Precio</th>
                  <th>Seña</th>
                  <th>Profesionales</th>
                  <th>Activo</th>
                  <th className="right" />
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => (
                  <React.Fragment key={g.categoria}>
                    <tr className="zr-table-cat-header">
                      <td colSpan={7}>
                        <b>{g.categoria}</b>
                      </td>
                    </tr>
                    {g.servicios.map((s) => {
                      const pros = obtenerProsDelServicio(s._id);
                      const numOverrides = contarOverrides(s._id);
                      const seleccionado = editando?._id === s._id;
                      const { variantes, adicionales } = resumenDeServicio(s);
                      const rango = variantes ? rangoDeDuracion(s) : null;
                      return (
                        <tr key={s._id} className={seleccionado ? "zr-row--selected" : undefined} aria-selected={seleccionado}>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 600 }}>{s.name}</span>
                              {numOverrides > 0 && (
                                <span className="zr-pill-override" title="Tiene precios o duraciones personalizados por profesional">
                                  {numOverrides === 1 ? "1 precio propio" : `${numOverrides} precios propios`}
                                </span>
                              )}
                              {variantes > 0 && <span className="zr-pill-override">{textoVariantes(variantes)}</span>}
                              {adicionales > 0 && <span className="zr-pill-override">{textoAdicionales(adicionales)}</span>}
                            </div>
                            {s.description && (
                              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{s.description}</div>
                            )}
                          </td>
                          <td className="num">
                            {rango && rango.min !== rango.max ? `${rango.min} a ${rango.max} min` : `${rango ? rango.min : s.durationMinutes} min`}
                            {Number(s.bufferAfterMinutes) > 0 && (
                              <span className="zr-prep-note">+ {s.bufferAfterMinutes} min de preparación</span>
                            )}
                          </td>
                          <td className="num">{variantes > 0 ? `Desde ${plata(precioDesde(s))}` : plata(s.price)}</td>
                          <td className="num">{s.deposit ? plata(s.deposit) : "Sin seña"}</td>
                          <td>
                            {pros.length === 0 ? (
                              <span className="ef-hint">—</span>
                            ) : (
                              <div className="zr-avatar-stack">
                                {pros.map((p, pIdx) => {
                                  const colorClass = AVATAR_COLORS[pIdx % AVATAR_COLORS.length];
                                  const inicial = (p.name || "P").charAt(0).toUpperCase();
                                  return (
                                    <span
                                      key={p._id}
                                      className={`zr-avatar zr-avatar--sm ${colorClass}`}
                                      title={p.name}
                                      aria-label={p.name}
                                    >
                                      {inicial}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              className="zr-switch"
                              aria-label={`${s.name} activo`}
                              checked={s.active !== false}
                              onChange={() => alternarActivo(s)}
                            />
                          </td>
                          <td className="right">
                            <button
                              type="button"
                              className="zr-btn zr-btn--ghost"
                              aria-label={`Editar ${s.name}`}
                              onClick={() => setEditor({ servicio: s })}
                            >
                              Editar
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editor && (
        <ServicioForm
          key={editando?._id || "nuevo"}
          servicio={editando}
          categorias={categorias}
          senasBloqueadas={senasBloqueadas}
          profesionales={profesionales}
          acciones={editando ? accionesDe(editando) : null}
          onClose={() => setEditor(null)}
          onSaved={guardado}
        />
      )}

      {aBorrar && (
        <ConfirmModal
          title="¿Eliminar servicio?"
          busy={borrando}
          onConfirm={confirmarBorrado}
          onCancel={() => setABorrar(null)}
        >
          Se eliminará <strong>{aBorrar.name}</strong>. Esta acción no se puede deshacer.
        </ConfirmModal>
      )}
      <OrgToast message={toast} onClose={cerrarToast} />
    </div>
  );
};

export default ServiciosTab;
